import { useCallback, useEffect, useState } from "react"

import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { SearchIcon } from "@/tiptap/icons/search-icon"

const DEFAULT_STATE = {
  query: "",
  matchCase: false,
  wholeWord: false,
  count: 0,
  current: -1,
  hasMatch: false,
  uiOpen: false,
}

function readStorage(editor) {
  const search = editor?.storage?.findAndReplace
  const ui = editor?.storage?.findReplace
  if (!search && !ui) return DEFAULT_STATE

  const results = search?.results ?? []
  const currentIndex = search?.currentIndex

  return {
    query: search?.searchTerm ?? "",
    matchCase: !!search?.caseSensitive,
    wholeWord: !!search?.wholeWord,
    count: results.length,
    current: currentIndex == null ? -1 : currentIndex,
    hasMatch: results.length > 0,
    uiOpen: !!ui?.uiOpen,
  }
}

// ProseMirror's tr.scrollIntoView is suppressed on touch in create-editor.js;
// scroll the current match decoration directly for find next/prev only.
function scrollCurrentMatchIntoView(editor) {
  if (!editor?.view?.dom || typeof window === "undefined") return

  const scroll = () => {
    const element = editor.view.dom.querySelector(".find-and-replace-result-current")
    if (!element || typeof element.scrollIntoView !== "function") return

    element.scrollIntoView({ block: "center", inline: "nearest" })
  }

  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(scroll)
  } else {
    scroll()
  }
}

export function useFindReplace(providedEditor) {
  const { editor } = useTiptapEditor(providedEditor)
  const [state, setState] = useState(() => readStorage(editor))

  useEffect(() => {
    if (!editor) return

    const sync = () => setState(readStorage(editor))

    sync()
    editor.on("transaction", sync)

    return () => {
      editor.off("transaction", sync)
    }
  }, [editor])

  const setQuery = useCallback((payload = {}) => {
    if (!editor) return false

    const { query, matchCase, wholeWord } = payload

    if (query !== undefined) {
      editor.commands.setSearchTerm(query)
    }
    if (matchCase !== undefined) {
      editor.commands.setCaseSensitive(matchCase)
    }
    if (wholeWord !== undefined) {
      editor.commands.setWholeWord(wholeWord)
    }

    return true
  }, [editor])

  const next = useCallback(() => {
    if (!editor) return false
    const moved = editor.commands.goToNextResult()
    if (moved) scrollCurrentMatchIntoView(editor)
    return moved
  }, [editor])

  const prev = useCallback(() => {
    if (!editor) return false
    const moved = editor.commands.goToPreviousResult()
    if (moved) scrollCurrentMatchIntoView(editor)
    return moved
  }, [editor])

  const replaceCurrent = useCallback((replace) => {
    if (!editor) return false
    editor.commands.setReplaceTerm(replace)
    return editor.commands.replace()
  }, [editor])

  const replaceAll = useCallback((replace) => {
    if (!editor) return false
    editor.commands.setReplaceTerm(replace)
    return editor.commands.replaceAll()
  }, [editor])

  const clear = useCallback(() => {
    if (!editor) return false
    return editor.commands.clearSearch()
  }, [editor])

  const open = useCallback(() => {
    if (!editor) return false
    return editor.commands.openFindReplace()
  }, [editor])

  const close = useCallback(() => {
    if (!editor) return false
    return editor.commands.closeFindReplace()
  }, [editor])

  return {
    editor,
    ...state,
    label: "Find and replace",
    Icon: SearchIcon,
    setQuery,
    next,
    prev,
    replaceCurrent,
    replaceAll,
    clear,
    open,
    close,
  }
}
